import type { Card, Choice } from '../types'

/**
 * 加载指定章节小节的卡片数据
 *
 * 加载后会对每张卡片的 choices 数组做 Fisher-Yates 洗牌,
 * 解决历史遗留的「正确答案 78% 集中在 A 选项」问题
 * (写卡时下意识把对的放第一个, 加载时随机打散到 A/B/C/D)
 *
 * isCorrect / feedback / ruleRef 都绑定在 Choice 对象上,
 * 所以洗牌后选项顺序随机, 但每个选项的含义保持不变
 */
export async function loadCards(
  chapterId: number,
  sectionId: string
): Promise<Card[]> {
  const path = `/cards/ch${chapterId}/${sectionId}.json`
  const response = await fetch(path)

  if (!response.ok) {
    throw new Error(`Failed to load cards: ${path} (${response.status})`)
  }

  const data = await response.json()

  let cards: Card[]
  // 支持两种格式：
  // 1. 直接是卡片数组
  if (Array.isArray(data)) {
    cards = data
  } else if (data.cards && Array.isArray(data.cards)) {
    // 2. 是包含 cards 字段的对象
    cards = data.cards
  } else {
    throw new Error(`Invalid card data format in ${path}`)
  }

  // 洗牌: 每张卡的 choices 原地打乱, 一次性随机化选项顺序
  for (const card of cards) {
    if (Array.isArray(card.choices) && card.choices.length > 1) {
      card.choices = shuffleChoices(card.choices)
    }
  }

  return cards
}

/**
 * Fisher-Yates 洗牌 —— 返回新数组, 不修改原数组
 * 保证 isCorrect 跟着 Choice 对象走, 选项语义不丢
 */
export function shuffleChoices(choices: Choice[]): Choice[] {
  const arr = [...choices]
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const tmp = arr[i]!
    arr[i] = arr[j]!
    arr[j] = tmp
  }
  return arr
}

/**
 * 批量加载多个小节
 */
export async function loadSectionCards(
  sectionIds: string[],
  chapterId: number
): Promise<Card[]> {
  const allCards: Card[] = []

  for (const sectionId of sectionIds) {
    const cards = await loadCards(chapterId, sectionId)
    allCards.push(...cards)
  }

  return allCards
}
